import { useCallback, useEffect, useState, useMemo } from "react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { api, authedFetch, API_BASE } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { cn, riskTone } from "@/lib/util";
import { toast } from "sonner";
import { Loader2, Play, Zap, Radar, ShieldAlert, Terminal, Globe, AlertTriangle, ChevronRight, X, CheckCircle2, Clock, CalendarClock, Pause, PlayCircle, Trash2, RotateCw, History, Download, Sparkles, ShieldCheck, GitCompare, Plus, Minus, Copy, FileDown, RefreshCw } from "lucide-react";
import ScanReportCard from "@/components/security/ScanReportCard";
import ScanComparisonModal from "@/components/security/ScanComparisonModal";
import AssetTargetPicker from "@/components/scanners/AssetTargetPicker";
import FullScanModal from "@/components/scanners/FullScanModal";
import { useActiveFullScan } from "@/lib/useActiveFullScan";
import { NucleiTemplatesUpdater } from "@/components/security/NucleiTemplatesUpdater";
import { KevCatalogPanel } from "@/components/security/KevCatalogPanel";
import { usePermissions } from "@/lib/permissions";

// Map tool.target_type → AssetTargetPicker.targetType
function pickerTypeFor(toolTargetType) {
  return {
    domain: "domain_only",
    host:   "domain_or_host",
    url:    "domain_or_host",
    any:    "any",
  }[toolTargetType] || "any";
}

const RISK_TONE = {
  low:    { text: "text-sev-low",      border: "border-sev-low/40",      bg: "bg-sev-low/10" },
  medium: { text: "text-sev-medium",   border: "border-sev-medium/40",   bg: "bg-sev-medium/10" },
  high:   { text: "text-sev-critical", border: "border-sev-critical/40", bg: "bg-sev-critical/10" },
};

const STATUS_TONE = {
  queued:  { text: "text-muted-foreground", label: "En cola",     icon: Clock },
  running: { text: "text-primary",          label: "Ejecutando",   icon: Loader2 },
  done:    { text: "text-sev-low",          label: "Completado",   icon: CheckCircle2 },
  error:   { text: "text-sev-critical",     label: "Error",        icon: AlertTriangle },
};

export default function Scanners() {
  const { t } = useT();
  const { role } = usePermissions();
  const isAdmin = role === "admin";
  const [tools, setTools] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [historyScheduleId, setHistoryScheduleId] = useState(null);
  const [showAltoPreset, setShowAltoPreset] = useState(false);
  const [showTIComboPreset, setShowTIComboPreset] = useState(false);
  const [showOsintDiff, setShowOsintDiff] = useState(false);
  const [showFullScan, setShowFullScan] = useState(false);
  const [activeFullScan, persistActiveFullScan] = useActiveFullScan();
  // Stable callback references so the modal's poll useEffect doesn't tear
  // down + restart on every parent render.
  const handleScanStart = useCallback(
    (runId, batchId) => persistActiveFullScan({ runId, batchId }),
    [persistActiveFullScan],
  );
  const handleScanComplete = useCallback(
    () => persistActiveFullScan(null),
    [persistActiveFullScan],
  );

  // Auto-open the Full-Scan modal when the user reaches this page from the
  // floating badge (Layout sets a sessionStorage flag or dispatches an event).
  useEffect(() => {
    if (sessionStorage.getItem("cibraxis:openFullScanOnMount") === "1") {
      sessionStorage.removeItem("cibraxis:openFullScanOnMount");
      setShowFullScan(true);
    }
    const onOpen = () => setShowFullScan(true);
    window.addEventListener("cibraxis:openFullScan", onOpen);
    return () => window.removeEventListener("cibraxis:openFullScan", onOpen);
  }, []);

  const altoClients = useMemo(
    () => clients.filter(c => (c.ens_level || "").toLowerCase() === "alto"),
    [clients]
  );

  const loadJobs = useCallback(async () => {
    const r = await api.get("/scans/jobs?limit=40");
    setJobs(r.data);
  }, []);
  const loadSchedules = useCallback(async () => {
    const r = await api.get("/scans/schedules");
    setSchedules(r.data);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/scans/tools"),
      api.get("/clients"),
      api.get("/scans/jobs?limit=40"),
      api.get("/scans/schedules"),
    ]).then(([tr, cr, jr, sr]) => {
      setTools(tr.data); setClients(cr.data); setJobs(jr.data); setSchedules(sr.data);
    });
  }, []);

  // Poll jobs every 3s while any is queued/running
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === "queued" || j.status === "running");
    if (!hasActive) return;
    const id = setInterval(() => { loadJobs(); loadSchedules(); }, 3000);
    return () => clearInterval(id);
  }, [jobs, loadJobs, loadSchedules]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scanners-page">
      <header>
        <div className="overline mb-2">Ethical Hacking · Active Scans</div>
        <h1 className="font-display font-bold tracking-tighter" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>
          Herramientas de escaneo activo
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Módulo de ethical hacking con las 8 herramientas open-source más usadas para auditoría de sitios web e IPs. 
          Todo escaneo requiere <span className="text-primary">autorización explícita</span> del cliente y solo actúa 
          sobre dominios/IPs previamente registrados. Los hallazgos se ingestan automáticamente y actualizan el 
          registro de cumplimiento ENS / ISO / NIST.
        </p>
      </header>

      <div className="border border-sev-medium/50 bg-sev-medium/10 text-sev-medium p-4 flex items-start gap-3" data-testid="ethical-notice">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="text-xs font-mono leading-relaxed">
          <div className="font-semibold tracking-wide uppercase mb-1">Aviso legal</div>
          El escaneo activo puede detonar IDS/IPS, generar tráfico saliente registrado y modificar contadores en servicios externos. 
          Al continuar declaras que dispones de <span className="text-foreground">autorización por escrito del cliente</span> 
          (contrato de auditoría / SOW / carta de compromiso) que abarca el objetivo y la ventana temporal seleccionados.
        </div>
      </div>

      {/* Configuración de fuentes de datos externas — visible para todos los
          roles autenticados; los botones de acción están gated por rol server-side. */}
      <div>
        <div className="overline mb-3">Configuración · Fuentes de datos externas</div>
        <div className="space-y-3">
          <NucleiTemplatesUpdater />
          <KevCatalogPanel canSync={isAdmin} />
        </div>
      </div>

      {/* Tool grid */}
      <div>
        <div className="overline mb-3">1 · Elige una herramienta</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {tools.map(tool => (
            <ToolCard key={tool.id} tool={tool}
                      selected={selectedTool?.id === tool.id}
                      onClick={() => setSelectedTool(tool)} />
          ))}
        </div>
      </div>

      {selectedTool && (
        <RunScanForm
          tool={selectedTool}
          clients={clients}
          onClose={() => setSelectedTool(null)}
          onQueued={() => { loadJobs(); toast.success("Escaneo encolado"); setSelectedTool(null); }}
          onScheduled={() => { loadSchedules(); toast.success("Programación creada"); setSelectedTool(null); }}
        />
      )}

      {/* Schedules */}
      <div>
        <div className="flex items-baseline justify-between mb-3 gap-4">
          <div>
            <div className="overline">2 · Programaciones automáticas</div>
            <div className="text-xs text-muted-foreground mt-1">
              Los escaneos programados se disparan cada 10, 30 o 60 minutos hasta que se pausen.
              Cuando la cola supera 20 jobs, las de 10 min se auto-pausan y se reanudan solas.
            </div>
          </div>
          <button
            onClick={() => setShowFullScan(true)}
            className="inline-flex items-center gap-2 border border-primary/60 bg-primary/15 text-primary px-3 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-primary/25 shadow-[0_0_0_1px_theme(colors.primary/40%)]"
            data-testid="full-scan-preset-btn"
            title="Lanza las 7 fases del preset Escaneo Completo sobre un cliente y objetivo"
          >
            <Zap className="w-3 h-3" /> Escaneo Completo
          </button>
          {altoClients.length > 0 && (
            <button
              onClick={() => setShowAltoPreset(true)}
              className="inline-flex items-center gap-2 border border-sev-critical/50 bg-sev-critical/10 text-sev-critical px-3 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-sev-critical/20"
              data-testid="ens-alto-preset-btn"
              title="Crea 4 schedules recomendados en un click para clientes categoría ALTO"
            >
              <ShieldCheck className="w-3 h-3" /> Batería ENS ALTO
            </button>
          )}
          <button
            onClick={() => setShowTIComboPreset(true)}
            className="inline-flex items-center gap-2 border border-sev-medium/50 bg-sev-medium/10 text-sev-medium px-3 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-sev-medium/20"
            data-testid="ti-combo-preset-btn"
            title="Lanza 4 fuentes de threat intel en paralelo (VT + AbuseIPDB + GreyNoise + HackerTarget) y consolida en 1 hallazgo"
          >
            <Radar className="w-3 h-3" /> Batería Threat Intel
          </button>
          <button
            onClick={() => setShowOsintDiff(true)}
            className="inline-flex items-center gap-2 border border-primary/50 bg-primary/10 text-primary px-3 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-primary/20"
            data-testid="osint-diff-btn"
            title="Compara los subdominios entre las últimas 2 corridas de Amass + Subfinder"
          >
            <GitCompare className="w-3 h-3" /> Comparador OSINT
          </button>
        </div>
        <SchedulesTable
          schedules={schedules}
          clients={clients}
          tools={tools}
          onChanged={() => { loadSchedules(); loadJobs(); }}
          onShowHistory={(id) => setHistoryScheduleId(id)}
        />
      </div>

      {/* Jobs list */}
      <div>
        <div className="overline mb-3">3 · Cronología de escaneos</div>
        {jobs.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            Aún no hay escaneos. Elige una herramienta arriba para empezar.
          </div>
        ) : (
          <div className="border border-border bg-card/60">
            <table className="w-full text-sm">
              <thead className="bg-background/50">
                <tr className="text-left overline border-b border-border">
                  <th className="p-3">Objetivo</th>
                  <th className="p-3">Cliente</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">Hallazgos</th>
                  <th className="p-3">Δ Nuevos</th>
                  <th className="p-3">Creado</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => {
                  const s = STATUS_TONE[j.status] || STATUS_TONE.queued;
                  const Icon = s.icon;
                  const client = clients.find(c => c.id === j.organization_id);
                  return (
                    <tr key={j.id} className="border-b border-border/50 hover:bg-muted/20 cursor-pointer"
                        onClick={() => setSelectedJob(j)}
                        data-testid={`scan-job-${j.id}`}>
                      <td className="p-3 font-mono text-xs truncate max-w-[220px]">
                        {j.target}
                        {j.kind === "full_scan_run" && (
                          <span className="ml-1.5 font-mono text-[9px] text-primary border border-primary/40 bg-primary/10 px-1.5 py-0.5"
                                title={`${j.tools_count || 0} herramientas ejecutadas en ${(j.phases_count || 7)} fases`}
                                data-testid={`scan-job-full-scan-badge-${j.id}`}>
                            FULL · {j.tools_count || 0} tools
                          </span>
                        )}
                        {isAdmin && j.kind !== "full_scan_run" && j.tool_id && (
                          <span className="ml-1.5 font-mono text-[9px] text-muted-foreground border border-border bg-muted/40 px-1.5 py-0.5 uppercase tracking-wider"
                                title={`Herramienta: ${tools.find(t => t.id === j.tool_id)?.label || j.tool_id}`}
                                data-testid={`scan-job-tool-badge-${j.id}`}>
                            {tools.find(t => t.id === j.tool_id)?.label || j.tool_id}
                          </span>
                        )}
                        {j.schedule_id && (
                          <span className="ml-1 font-mono text-[9px] text-muted-foreground" title="Ejecutado por programación">
                            · <CalendarClock className="w-2.5 h-2.5 inline" />
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs">{client?.name || "—"}</td>
                      <td className="p-3">
                        <span className={cn("inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase", s.text)}>
                          <Icon className={cn("w-3 h-3", j.status === "running" && "animate-spin")} />
                          {s.label}
                        </span>
                      </td>
                      <td className="p-3 font-mono">
                        {j.findings_count > 0 ? (
                          <span className="text-sev-critical">{j.findings_count}</span>
                        ) : "—"}
                      </td>
                      <td className="p-3 font-mono">
                        {j.delta_new > 0 ? (
                          <span className="inline-flex items-center gap-1 text-sev-critical font-bold"
                                data-testid={`job-delta-new-${j.id}`}
                                title={`${j.delta_new} nuevos · ${j.delta_resolved || 0} resueltos`}>
                            <Sparkles className="w-3 h-3" /> +{j.delta_new}
                          </span>
                        ) : j.delta_resolved > 0 ? (
                          <span className="text-sev-low text-xs">−{j.delta_resolved}</span>
                        ) : "—"}
                      </td>
                      <td className="p-3 font-mono text-[10px] text-muted-foreground">
                        {new Date(j.created_at).toLocaleString()}
                      </td>
                      <td className="p-3"><ChevronRight className="w-3 h-3 text-muted-foreground" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedJob && <JobDetailModal jobId={selectedJob.id} onClose={() => setSelectedJob(null)} />}
      {historyScheduleId && (
        <ScheduleHistoryModal
          scheduleId={historyScheduleId}
          clients={clients}
          tools={tools}
          onClose={() => setHistoryScheduleId(null)}
        />
      )}
      {showAltoPreset && (
        <ENSAltoPresetModal
          clients={altoClients}
          onClose={() => setShowAltoPreset(false)}
          onCreated={() => { loadSchedules(); setShowAltoPreset(false); }}
        />
      )}
      {showOsintDiff && (
        <OSINTDiffModal
          clients={clients}
          onClose={() => setShowOsintDiff(false)}
        />
      )}
      {showTIComboPreset && (
        <ThreatIntelComboModal
          clients={clients}
          onClose={() => setShowTIComboPreset(false)}
          onQueued={() => { loadJobs(); setShowTIComboPreset(false); }}
        />
      )}
      {showFullScan && (
        <FullScanModal
          clients={clients}
          initialRunId={activeFullScan?.runId || null}
          initialBatchId={activeFullScan?.batchId || null}
          onScanStart={handleScanStart}
          onScanComplete={handleScanComplete}
          onClose={() => setShowFullScan(false)}
          onLaunched={() => { loadJobs(); }}
        />
      )}
      {/* Floating badge is rendered globally by Layout.jsx via useActiveFullScan hook. */}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ToolCard({ tool, selected, onClick }) {
  const tone = RISK_TONE[tool.risk] || RISK_TONE.medium;
  const Icon = { port_scan: Radar, vuln_scan: ShieldAlert, tls_scan: ShieldAlert,
                 recon: Globe }[tool.kind] || Terminal;
  return (
    <button onClick={onClick}
            className={cn(
              "border p-4 text-left transition-all bg-card/60 hover:bg-card",
              selected ? "border-primary bg-primary/10" : "border-border",
            )}
            data-testid={`tool-${tool.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-display text-base font-medium">{tool.label}</span>
        </div>
        <span className={cn("font-mono text-[9px] tracking-widest uppercase border px-1.5 py-0.5",
                            tone.text, tone.border, tone.bg)}>
          {tool.risk}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-2 leading-snug">{tool.description}</p>
      <div className="mt-3 font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
        {tool.kind.replace("_", " ")} · ~{tool.avg_seconds}s
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
function RunScanForm({ tool, clients, onClose, onQueued, onScheduled }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [target, setTarget] = useState("");
  const [pickerTargets, setPickerTargets] = useState([]);
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("once"); // once | schedule
  const [intervalMin, setIntervalMin] = useState(30);
  // Per-tool options — currently only Nuclei exposes a verbose/debug switch
  // to log which templates were loaded and executed.
  const [nucleiVerbose, setNucleiVerbose] = useState(false);
  const client = clients.find(c => c.id === clientId);
  const isAll = target === "__ALL__";
  const expandedTargets = isAll ? pickerTargets.map(t => t.value)
                                 : (target ? [target] : []);

  useEffect(() => { setTarget(""); }, [clientId]);

  const buildOptions = () => {
    if (tool.id === "nuclei" && nucleiVerbose) return { verbose: true };
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!authorized || expandedTargets.length === 0) return;
    setBusy(true);
    let ok = 0, fail = 0;
    const options = buildOptions();
    try {
      for (const tgt of expandedTargets) {
        try {
          if (mode === "once") {
            await api.post("/scans/run", {
              client_id: clientId, tool_id: tool.id,
              target: tgt, authorized: true,
              ...(options ? { options } : {}),
            });
          } else {
            await api.post("/scans/schedules", {
              client_id: clientId, tool_id: tool.id,
              target: tgt, authorized: true,
              interval_minutes: intervalMin,
            });
          }
          ok++;
        } catch (ex) {
          fail++;
          console.warn("scan failed for", tgt, ex?.response?.data?.detail);
        }
      }
      if (ok > 0) {
        toast.success(
          expandedTargets.length === 1
            ? (mode === "once" ? "Escaneo encolado" : "Programación creada")
            : `${ok} objetivos ${mode === "once" ? "encolados" : "programados"}${fail ? ` · ${fail} fallaron` : ""}`
        );
        mode === "once" ? onQueued() : onScheduled();
      } else {
        toast.error("No se pudo encolar ningún escaneo");
      }
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-primary/40 bg-primary/5 p-5 space-y-4 relative" data-testid="run-scan-form">
      <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1 hover:bg-muted"
              data-testid="close-run-form">
        <X className="w-3 h-3" />
      </button>
      <div>
        <div className="overline">Configurar {tool.label}</div>
        <div className="text-xs text-muted-foreground mt-1">{tool.description}</div>
      </div>

      {/* Mode selector */}
      <div className="flex gap-1 border border-border p-1 w-fit">
        <button type="button" onClick={() => setMode("once")}
                className={cn("font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 flex items-center gap-1",
                  mode === "once" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
                data-testid="mode-once">
          <Play className="w-3 h-3" /> Una vez
        </button>
        <button type="button" onClick={() => setMode("schedule")}
                className={cn("font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 flex items-center gap-1",
                  mode === "schedule" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
                data-testid="mode-schedule">
          <CalendarClock className="w-3 h-3" /> Programado
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <div className="overline mb-1">Cliente</div>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                  className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                  data-testid="scan-client-select">
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="overline mb-1">Objetivo ({tool.target_type})</div>
          <AssetTargetPicker clientId={clientId} value={target}
                             onChange={(v, list) => { setTarget(v); setPickerTargets(list); }}
                             targetType={pickerTypeFor(tool.target_type)}
                             allowAll={true}
                             testId="scan-target-picker" />
        </label>
      </div>

      {mode === "schedule" && (
        <div>
          <div className="overline mb-1">Frecuencia</div>
          <div className="flex gap-2">
            {[
              { m: 10, label: "Cada 10 min" },
              { m: 30, label: "Cada 30 min" },
              { m: 60, label: "Cada 1 hora" },
            ].map(opt => (
              <button key={opt.m} type="button" onClick={() => setIntervalMin(opt.m)}
                      className={cn(
                        "font-mono text-[10px] tracking-widest uppercase px-4 py-2 border flex-1 flex items-center justify-center gap-2",
                        intervalMin === opt.m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-muted-foreground"
                      )}
                      data-testid={`interval-${opt.m}`}>
                <Clock className="w-3 h-3" /> {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {client && (
        <div className="text-[10px] font-mono text-muted-foreground">
          Alcance autorizado para {client.name}: {(client.root_domains || []).join(", ") || "sin dominios"} · {(client.ip_ranges || []).join(", ") || "sin IPs"}
        </div>
      )}

      {tool.id === "nuclei" && (
        <div className="border border-border p-3 bg-card/40 space-y-2" data-testid="nuclei-options">
          <div className="overline">Opciones · Nuclei</div>
          <label className="flex items-start gap-3 text-xs cursor-pointer">
            <input type="checkbox" checked={nucleiVerbose}
                   onChange={(e) => setNucleiVerbose(e.target.checked)}
                   data-testid="nuclei-verbose-toggle"
                   className="mt-0.5 accent-primary" />
            <span>
              <b className="text-foreground">Modo debug / verbose</b> — registra qué templates se cargan y ejecutan
              (versión, cantidad, clustering) y expone el banner completo en el panel de resultados.
              Útil para confirmar que corre el set community completo, no un subconjunto mínimo.
            </span>
          </label>
        </div>
      )}

      <label className="flex items-start gap-3 text-xs cursor-pointer">
        <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)}
               data-testid="scan-auth-checkbox"
               className="mt-0.5 accent-primary" />
        <span>
          Declaro que dispongo de <b>autorización escrita</b> del cliente para ejecutar {tool.label} sobre <code className="font-mono text-primary">{isAll ? `${expandedTargets.length} activos` : (target || "el objetivo")}</code>
          {mode === "schedule" && <> de forma <b>recurrente cada {intervalMin} minutos</b></>}
          , y que {isAll ? "todos" : "el objetivo"} está{isAll ? "n" : ""} dentro del alcance del contrato de auditoría vigente.
        </span>
      </label>
      <div className="flex justify-end">
        <button type="submit" disabled={busy || !authorized || expandedTargets.length === 0 || !clientId}
                className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="scan-submit-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : mode === "once" ? <Play className="w-3 h-3" /> : <CalendarClock className="w-3 h-3" />}
          {mode === "once"
            ? (isAll ? `Encolar ${expandedTargets.length} escaneos` : "Encolar escaneo")
            : (isAll ? `Crear ${expandedTargets.length} programaciones` : "Crear programación")}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
function SchedulesTable({ schedules, clients, tools, onChanged, onShowHistory }) {
  if (schedules.length === 0) {
    return (
      <div className="border border-dashed border-border p-6 text-center text-muted-foreground text-xs">
        Sin programaciones activas. Elige una herramienta arriba y selecciona <span className="text-primary">Programado</span> para crear la primera.
      </div>
    );
  }
  return (
    <div className="border border-border bg-card/60" data-testid="schedules-table">
      <table className="w-full text-sm">
        <thead className="bg-background/50">
          <tr className="text-left overline border-b border-border">
            <th className="p-3">Estado</th>
            <th className="p-3">Herramienta</th>
            <th className="p-3">Objetivo</th>
            <th className="p-3">Cliente</th>
            <th className="p-3">Frecuencia</th>
            <th className="p-3">Próxima ejec.</th>
            <th className="p-3">Última ejec.</th>
            <th className="p-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map(s => (
            <ScheduleRow key={s.id} s={s} clients={clients} tools={tools}
                         onChanged={onChanged} onShowHistory={onShowHistory} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleRow({ s, clients, tools, onChanged, onShowHistory }) {
  const [busy, setBusy] = useState(false);
  const client = clients.find(c => c.id === s.organization_id);
  const tool = tools.find(t => t.id === s.tool_id);
  const isAutoPaused = !s.enabled && s.auto_paused;
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/scans/schedules/${s.id}`, { enabled: !s.enabled });
      toast.success(!s.enabled ? "Programación reanudada" : "Programación pausada");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const changeInterval = async (interval_minutes) => {
    setBusy(true);
    try {
      await api.patch(`/scans/schedules/${s.id}`, { interval_minutes });
      toast.success(`Frecuencia actualizada a ${interval_minutes} min`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try {
      await api.post(`/scans/schedules/${s.id}/run-now`);
      toast.success("Ejecución manual encolada");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar la programación de ${s.tool_id} sobre ${s.target}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/scans/schedules/${s.id}`);
      toast.success("Programación eliminada");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <tr className="border-b border-border/50 hover:bg-muted/20" data-testid={`schedule-${s.id}`}>
      <td className="p-3">
        <div className="flex flex-col gap-1">
          <button onClick={toggle} disabled={busy}
                  className={cn("inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border w-fit",
                    s.enabled ? "text-sev-low border-sev-low/50 bg-sev-low/10" : "text-muted-foreground border-border")}
                  data-testid={`schedule-toggle-${s.id}`}>
            {s.enabled ? <PlayCircle className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
            {s.enabled ? "Activa" : "Pausada"}
          </button>
          {isAutoPaused && (
            <span className="inline-flex items-center gap-1 font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 border border-sev-medium/40 text-sev-medium bg-sev-medium/10 w-fit"
                  title="Pausada automáticamente por sobrecarga de cola"
                  data-testid={`schedule-auto-paused-${s.id}`}>
              <AlertTriangle className="w-2.5 h-2.5" /> AUTO-PAUSA
            </span>
          )}
        </div>
      </td>
      <td className="p-3 font-mono text-xs">{tool?.label || s.tool_id}</td>
      <td className="p-3 font-mono text-xs truncate max-w-[200px]">{s.target}</td>
      <td className="p-3 text-xs">{client?.name || "—"}</td>
      <td className="p-3">
        <div className="flex gap-1">
          {[10, 30, 60].map(m => (
            <button key={m} onClick={() => changeInterval(m)} disabled={busy || s.interval_minutes === m}
                    className={cn("font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 border",
                      s.interval_minutes === m ? "border-primary text-primary bg-primary/10 cursor-default" : "border-border text-muted-foreground hover:border-muted-foreground")}
                    data-testid={`schedule-interval-${s.id}-${m}`}>
              {m}m
            </button>
          ))}
        </div>
      </td>
      <td className="p-3 font-mono text-[10px] text-muted-foreground">
        {s.enabled ? new Date(s.next_run_at).toLocaleString() : "—"}
      </td>
      <td className="p-3 font-mono text-[10px] text-muted-foreground">
        {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "nunca"}
      </td>
      <td className="p-3 text-right">
        <div className="inline-flex gap-1">
          <button onClick={() => onShowHistory(s.id)} disabled={busy} title="Historial (últimas 20 ejecuciones)"
                  className="p-1 border border-border hover:border-primary hover:text-primary"
                  data-testid={`schedule-history-${s.id}`}>
            <History className="w-3 h-3" />
          </button>
          <button onClick={runNow} disabled={busy} title="Ejecutar ahora"
                  className="p-1 border border-border hover:border-primary hover:text-primary"
                  data-testid={`schedule-run-now-${s.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
          </button>
          <button onClick={remove} disabled={busy} title="Eliminar"
                  className="p-1 border border-border hover:border-sev-critical hover:text-sev-critical"
                  data-testid={`schedule-delete-${s.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
function JobDetailModal({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const r = await api.get(`/scans/jobs/${jobId}`);
      if (alive) setJob(r.data);
    };
    load();
    const id = setInterval(load, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [jobId]);

  if (!job) return null;
  const s = STATUS_TONE[job.status] || STATUS_TONE.queued;
  const Icon = s.icon;

  const downloadPdf = async () => {
    try {
      const r = await authedFetch(`${API_BASE}/scans/jobs/${job.id}/pdf`);
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scan-${(job.target || "target").replace(/[^\w.-]/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-4xl bg-card border border-border max-h-[90vh] flex flex-col"
           data-testid="job-detail-modal">
        <div className="p-5 border-b border-border flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="overline mb-1 flex items-center gap-2">
              Módulo · {job.status}
              <Icon className={cn("w-3 h-3", job.status === "running" && "animate-spin", s.text)} />
            </div>
            <h2 className="font-display text-2xl font-medium truncate">{job.target}</h2>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              Iniciado {job.started_at ? new Date(job.started_at).toLocaleString() : "—"} ·
              Terminado {job.finished_at ? new Date(job.finished_at).toLocaleString() : "—"} ·
              {job.findings_count || 0} hallazgos ingestados
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {job.status === "done" && (
              <>
                <button onClick={() => setShowCompare(true)}
                        data-testid="scan-compare-btn"
                        className="border border-primary/50 text-primary bg-primary/10 px-3 py-1.5 text-xs font-mono tracking-widest uppercase hover:bg-primary/20 inline-flex items-center gap-1.5">
                  <GitCompare className="w-3 h-3" /> Comparar
                </button>
                <button onClick={downloadPdf}
                        data-testid="scan-download-pdf-btn"
                        className="border border-primary/50 text-primary bg-primary/10 px-3 py-1.5 text-xs font-mono tracking-widest uppercase hover:bg-primary/20 inline-flex items-center gap-1.5">
                  <FileDown className="w-3 h-3" /> PDF
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-job-modal">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <ScanReportCard job={job} />
        </div>
      </div>
      {showCompare && (
        <ScanComparisonModal jobId={job.id} onClose={() => setShowCompare(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ScheduleHistoryModal({ scheduleId, clients, tools, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    setBusy(true);
    api.get(`/scans/schedules/${scheduleId}/history?limit=20`)
      .then(r => setData(r.data))
      .catch(() => toast.error("No se pudo cargar el historial"))
      .finally(() => setBusy(false));
  }, [scheduleId]);

  const sched = data?.schedule;
  const runs = (data?.runs || []).slice().reverse(); // oldest → newest for sparkline
  const client = sched && clients.find(c => c.id === sched.organization_id);
  const tool = sched && tools.find(t => t.id === sched.tool_id);

  const chartData = runs.map((r, i) => ({
    idx: i + 1,
    delta_new: r.delta_new || 0,
    duration_s: r.duration_ms ? Math.round(r.duration_ms / 1000) : 0,
    findings: r.findings_count || 0,
    when: r.finished_at || r.created_at,
  }));

  const totals = runs.reduce((acc, r) => {
    acc.new_total += (r.delta_new || 0);
    acc.resolved_total += (r.delta_resolved || 0);
    acc.duration_avg += r.duration_ms || 0;
    if (r.status === "done") acc.ok += 1;
    if (r.status === "error") acc.err += 1;
    return acc;
  }, { new_total: 0, resolved_total: 0, duration_avg: 0, ok: 0, err: 0 });
  totals.duration_avg = runs.length ? Math.round(totals.duration_avg / runs.length / 1000) : 0;

  const downloadCsv = async () => {
    try {
      const res = await authedFetch(`${API_BASE}/scans/schedules/${scheduleId}/history.csv`);
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-${scheduleId.slice(0, 8)}-history.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar el CSV");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-5xl bg-card border border-border max-h-[90vh] flex flex-col"
           data-testid="schedule-history-modal">
        <div className="p-5 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="overline mb-1 flex items-center gap-2">
              <History className="w-3 h-3" /> Historial de ejecuciones
            </div>
            <h2 className="font-display text-2xl font-medium">
              {tool?.label || sched?.tool_id} · {sched?.target}
            </h2>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              Cliente: {client?.name || "—"} · Cada {sched?.interval_minutes || "?"} min ·
              {runs.length} ejecuciones registradas
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadCsv}
                    className="inline-flex items-center gap-2 border border-border hover:border-primary hover:text-primary px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase"
                    data-testid="history-export-csv">
              <Download className="w-3 h-3" /> Exportar CSV
            </button>
            <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-history-modal">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {busy ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando historial…
            </div>
          ) : runs.length === 0 ? (
            <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
              Aún no hay ejecuciones registradas para esta programación.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Nuevos hallazgos" value={totals.new_total} tone="sev-critical" />
                <StatCard label="Resueltos" value={totals.resolved_total} tone="sev-low" />
                <StatCard label="Duración media" value={`${totals.duration_avg}s`} />
                <StatCard label="OK / Error" value={`${totals.ok} / ${totals.err}`}
                          tone={totals.err > 0 ? "sev-medium" : "sev-low"} />
              </div>

              <div>
                <div className="overline mb-2">Δ Nuevos hallazgos por ejecución</div>
                <div className="border border-border bg-background p-3 h-32" data-testid="history-sparkline">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{ background: "#0b0b0e", border: "1px solid #333",
                                       fontFamily: "monospace", fontSize: 11 }}
                        formatter={(v) => `${v} nuevos`}
                        labelFormatter={(l, p) => p[0] ? new Date(p[0].payload.when).toLocaleString() : ""}
                      />
                      <Line type="monotone" dataKey="delta_new" stroke="#f87171"
                            strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <div className="overline mb-2">Duración por ejecución (segundos)</div>
                <div className="border border-border bg-background p-3 h-32" data-testid="history-duration-sparkline">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{ background: "#0b0b0e", border: "1px solid #333",
                                       fontFamily: "monospace", fontSize: 11 }}
                        formatter={(v) => `${v}s`}
                        labelFormatter={(l, p) => p[0] ? new Date(p[0].payload.when).toLocaleString() : ""}
                      />
                      <Line type="monotone" dataKey="duration_s" stroke="#60a5fa"
                            strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <div className="overline mb-2">Últimas {runs.length} ejecuciones</div>
                <div className="border border-border bg-card/60">
                  <table className="w-full text-sm" data-testid="history-runs-table">
                    <thead className="bg-background/50">
                      <tr className="text-left overline border-b border-border">
                        <th className="p-2">Cuándo</th>
                        <th className="p-2">Estado</th>
                        <th className="p-2">Duración</th>
                        <th className="p-2">Hallazgos</th>
                        <th className="p-2">Δ Nuevos</th>
                        <th className="p-2">Δ Resueltos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...runs].reverse().map(r => {
                        const st = STATUS_TONE[r.status] || STATUS_TONE.queued;
                        const Icon = st.icon;
                        return (
                          <tr key={r.id} className="border-b border-border/50">
                            <td className="p-2 font-mono text-[10px] text-muted-foreground">
                              {new Date(r.finished_at || r.created_at).toLocaleString()}
                            </td>
                            <td className="p-2">
                              <span className={cn("inline-flex items-center gap-1 font-mono text-[10px]", st.text)}>
                                <Icon className="w-3 h-3" /> {st.label}
                              </span>
                            </td>
                            <td className="p-2 font-mono text-xs">
                              {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}
                            </td>
                            <td className="p-2 font-mono">{r.findings_count || 0}</td>
                            <td className="p-2 font-mono">
                              {r.delta_new > 0 ? (
                                <span className="text-sev-critical font-bold">+{r.delta_new}</span>
                              ) : "—"}
                            </td>
                            <td className="p-2 font-mono">
                              {r.delta_resolved > 0 ? (
                                <span className="text-sev-low">−{r.delta_resolved}</span>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneClass = tone === "sev-critical" ? "text-sev-critical border-sev-critical/40"
                  : tone === "sev-low" ? "text-sev-low border-sev-low/40"
                  : tone === "sev-medium" ? "text-sev-medium border-sev-medium/40"
                  : "text-foreground border-border";
  return (
    <div className={cn("border p-3 bg-background/40", toneClass)}>
      <div className="overline text-[9px]">{label}</div>
      <div className="font-display text-2xl font-medium mt-1">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ENSAltoPresetModal({ clients, onClose, onCreated }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [target, setTarget] = useState("");
  const [pickerTargets, setPickerTargets] = useState([]);
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const client = clients.find(c => c.id === clientId);
  const isAll = target === "__ALL__";
  const domainTargets = pickerTargets.filter(t => t.kind === "domain");
  const expanded = isAll ? domainTargets.map(t => t.value)
                          : (target ? [target] : []);

  useEffect(() => { setTarget(""); }, [clientId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!authorized || expanded.length === 0) return;
    setBusy(true);
    let totalCreated = 0, totalExisting = 0, fail = 0;
    try {
      for (const tgt of expanded) {
        try {
          const r = await api.post("/scans/schedules/preset/ens-alto", {
            client_id: clientId, target: tgt, authorized: true,
          });
          totalCreated += r.data.created_count || 0;
          totalExisting += r.data.existing_count || 0;
        } catch { fail++; }
      }
      if (fail === expanded.length) {
        toast.error("No se pudo crear ninguna batería");
      } else {
        toast.success(`Batería ENS ALTO · ${totalCreated} nuevos, ${totalExisting} ya existían${fail ? ` · ${fail} objetivos fallaron` : ""}`);
        onCreated();
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-2xl bg-card border border-border p-6 space-y-4"
            data-testid="ens-alto-preset-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-1 flex items-center gap-2 text-sev-critical">
              <ShieldCheck className="w-3 h-3" /> Preset ENS categoría ALTO
            </div>
            <h2 className="font-display text-2xl font-medium">Batería completa · 4 programaciones</h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-md">
              Crea en un solo click 4 escaneos recurrentes recomendados para sistemas categoría ALTO
              del Anexo I del RD 311/2022:
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-preset-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="border border-border bg-background/50 p-3 space-y-1 text-xs font-mono">
          <div className="flex justify-between"><span>· nmap</span><span className="text-muted-foreground">cada 60 min · puertos + servicios</span></div>
          <div className="flex justify-between"><span>· nuclei</span><span className="text-muted-foreground">cada 30 min · vulnerabilidades (templates)</span></div>
          <div className="flex justify-between"><span>· testssl</span><span className="text-muted-foreground">cada 60 min · TLS/SSL config</span></div>
          <div className="flex justify-between"><span>· httpx</span><span className="text-muted-foreground">cada 30 min · fingerprint + headers</span></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="overline mb-1">Cliente ENS ALTO</div>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                    data-testid="preset-client-select">
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="overline mb-1">Objetivo (dominio raíz)</div>
            <AssetTargetPicker clientId={clientId} value={target}
                               onChange={(v, list) => { setTarget(v); setPickerTargets(list); }}
                               targetType="domain_only"
                               allowAll={true}
                               testId="preset-target-picker" />
          </label>
        </div>

        {client && (
          <div className="text-[10px] font-mono text-muted-foreground">
            Alcance autorizado para {client.name}: {(client.root_domains || []).join(", ") || "sin dominios"}
          </div>
        )}

        <label className="flex items-start gap-3 text-xs cursor-pointer">
          <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)}
                 data-testid="preset-auth-checkbox"
                 className="mt-0.5 accent-primary" />
          <span>
            Declaro que dispongo de <b>autorización escrita</b> del cliente para ejecutar
            los 4 escaneos recurrentes sobre <code className="font-mono text-primary">{isAll ? `${expanded.length} dominios` : (target || "el objetivo")}</code>,
            y que están dentro del alcance del contrato de auditoría vigente.
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </button>
          <button type="submit" disabled={busy || !authorized || expanded.length === 0 || !clientId}
                  className="bg-sev-critical text-primary-foreground px-4 py-2 text-sm hover:bg-sev-critical/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="preset-submit-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
            {isAll ? `Crear ${expanded.length * 4} schedules` : "Crear 4 schedules"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ---------------------------------------------------------------------------
function OSINTDiffModal({ clients, onClose }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [target, setTarget] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const client = clients.find(c => c.id === clientId);

  useEffect(() => { setTarget(""); setData(null); }, [clientId]);

  const runCompare = async () => {
    if (!clientId || !target || target === "__ALL__") return;
    setBusy(true);
    setData(null);
    try {
      const r = await api.get(`/scans/osint-diff?client_id=${clientId}&target=${encodeURIComponent(target)}&tools=amass,subfinder,hackertarget,securitytrails`);
      setData(r.data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo comparar");
    } finally {
      setBusy(false);
    }
  };

  const copyList = (arr, kind) => {
    if (!arr?.length) return;
    navigator.clipboard.writeText(arr.join("\n"))
      .then(() => toast.success(`${arr.length} ${kind} copiado(s) al portapapeles`))
      .catch(() => toast.error("No se pudo copiar"));
  };

  const filterFn = (list) => {
    const q = filter.trim().toLowerCase();
    return q ? list.filter(s => s.includes(q)) : list;
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-6xl bg-card border border-border max-h-[92vh] flex flex-col"
           data-testid="osint-diff-modal">
        <div className="p-5 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="overline mb-1 flex items-center gap-2 text-primary">
              <GitCompare className="w-3 h-3" /> Comparador OSINT · 4 fuentes
            </div>
            <h2 className="font-display text-2xl font-medium">Diff de subdominios entre corridas</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Compara la última ejecución vs la anterior sobre el mismo dominio agregando 4 fuentes OSINT.
              Verás qué subdominios <span className="text-sev-low">aparecieron</span>, cuáles <span className="text-sev-critical">desaparecieron</span>, cuáles se mantienen estables
              y cuáles fueron detectados <b>únicamente</b> por una fuente (útil para valorar cobertura por proveedor).
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-osint-diff">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 border-b border-border grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <label className="block">
            <div className="overline mb-1">Cliente</div>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                    data-testid="osint-diff-client-select">
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="overline mb-1">Dominio raíz</div>
            <AssetTargetPicker clientId={clientId} value={target}
                               onChange={(v) => setTarget(v)}
                               targetType="domain_only"
                               allowAll={false}
                               testId="osint-diff-target-picker" />
          </label>
          <button onClick={runCompare} disabled={busy || !clientId || !target || target === "__ALL__"}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm font-mono tracking-widest uppercase hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="osint-diff-run-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitCompare className="w-3 h-3" />}
            Comparar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!data && !busy && (
            <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
              Elige un cliente y dominio y pulsa <span className="text-primary">Comparar</span> para ver el diff.
            </div>
          )}
          {busy && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando corridas…
            </div>
          )}
          {data && (
            <>
              {/* Meta strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Subdominios actuales" value={data.current.count} />
                <StatCard label="Subdominios previos" value={data.previous.count} />
                <StatCard label="Nuevos (added)" value={data.diff.added_count} tone="sev-low" />
                <StatCard label="Desaparecidos" value={data.diff.removed_count} tone="sev-critical" />
              </div>

              {/* Runs metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[10px] font-mono">
                <div className="border border-border bg-background/40 p-3">
                  <div className="overline mb-2">Última corrida (baseline actual)</div>
                  {data.current.runs.length === 0 ? (
                    <div className="text-muted-foreground">Sin ejecuciones registradas — corre un módulo OSINT primero.</div>
                  ) : data.current.runs.map((r, i) => (
                    <div key={r.job_id} className="flex justify-between py-0.5">
                      <span className="text-primary">Fuente {i + 1}</span>
                      <span className="text-muted-foreground">{new Date(r.finished_at).toLocaleString()}</span>
                      <span>{r.count} subs</span>
                    </div>
                  ))}
                </div>
                <div className="border border-border bg-background/40 p-3">
                  <div className="overline mb-2">Corrida anterior</div>
                  {data.previous.runs.length === 0 ? (
                    <div className="text-muted-foreground">Sin corrida anterior — ejecuta otra vez para tener baseline.</div>
                  ) : data.previous.runs.map((r, i) => (
                    <div key={r.job_id} className="flex justify-between py-0.5">
                      <span className="text-muted-foreground">Fuente {i + 1}</span>
                      <span className="text-muted-foreground">{new Date(r.finished_at).toLocaleString()}</span>
                      <span>{r.count} subs</span>
                    </div>
                  ))}
                </div>
              </div>

              {!data.has_baseline && (
                <div className="border border-sev-medium/40 bg-sev-medium/10 text-sev-medium p-3 text-xs font-mono">
                  Aún no hay corrida anterior para comparar. Ejecuta los módulos OSINT una vez más y vuelve para ver el diff completo.
                </div>
              )}

              {/* Per-provider unique (cross-source uniqueness) */}
              {data.per_provider_stats && data.per_provider_stats.length > 1 && (
                <div className="border border-primary/30 bg-primary/5 p-3" data-testid="per-provider-panel">
                  <div className="overline mb-2 text-primary">Cobertura por fuente (subdominios únicos vs. compartidos)</div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px] font-mono">
                    {data.per_provider_stats.map((s, i) => (
                      <div key={s.tool_id} className="border border-border p-2 bg-background/40">
                        <div className="text-primary uppercase tracking-widest">Fuente {i + 1}</div>
                        <div className="mt-1"><span className="text-foreground">{s.found}</span> subs totales</div>
                        <div><span className="text-sev-low">{s.unique_to_this_provider}</span> únicos aquí</div>
                        <div><span className="text-muted-foreground">{s.shared_with_others}</span> compartidos</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filter */}
              <div className="flex items-center gap-2">
                <input value={filter} onChange={(e) => setFilter(e.target.value)}
                       placeholder="Filtrar subdominios…"
                       className="w-full max-w-xs bg-background border border-border px-3 py-1.5 text-xs font-mono focus:border-primary outline-none"
                       data-testid="osint-diff-filter" />
              </div>

              {/* Diff columns */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="osint-diff-lists">
                <DiffColumn
                  title="Nuevos"
                  icon={<Plus className="w-3 h-3" />}
                  list={filterFn(data.diff.added)}
                  tone="sev-low"
                  onCopy={() => copyList(data.diff.added, "nuevos")}
                  testid="diff-col-added"
                />
                <DiffColumn
                  title="Desaparecidos"
                  icon={<Minus className="w-3 h-3" />}
                  list={filterFn(data.diff.removed)}
                  tone="sev-critical"
                  onCopy={() => copyList(data.diff.removed, "desaparecidos")}
                  testid="diff-col-removed"
                />
                <DiffColumn
                  title="Estables"
                  icon={<CheckCircle2 className="w-3 h-3" />}
                  list={filterFn(data.diff.stable)}
                  tone="muted"
                  onCopy={() => copyList(data.diff.stable, "estables")}
                  testid="diff-col-stable"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DiffColumn({ title, icon, list, tone, onCopy, testid }) {
  const toneClass = tone === "sev-low" ? "border-sev-low/40 text-sev-low"
                  : tone === "sev-critical" ? "border-sev-critical/40 text-sev-critical"
                  : "border-border text-muted-foreground";
  return (
    <div className={cn("border bg-background/40 flex flex-col", toneClass)} data-testid={testid}>
      <div className="flex items-center justify-between p-2 border-b border-current/30">
        <div className="overline flex items-center gap-1.5">
          {icon} {title} · {list.length}
        </div>
        <button onClick={onCopy} disabled={!list.length}
                className="p-1 hover:bg-current/10 disabled:opacity-30"
                title="Copiar lista al portapapeles"
                data-testid={`${testid}-copy`}>
          <Copy className="w-3 h-3" />
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto p-2 space-y-0.5">
        {list.length === 0 ? (
          <div className="text-muted-foreground text-xs font-mono italic py-4 text-center">—</div>
        ) : (
          list.slice(0, 300).map(s => (
            <div key={s} className="font-mono text-[11px] text-foreground truncate hover:text-primary" title={s}>
              {s}
            </div>
          ))
        )}
        {list.length > 300 && (
          <div className="text-muted-foreground text-[10px] font-mono italic pt-2 text-center">
            … +{list.length - 300} más (usa filtro o copia para verlos)
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
function ThreatIntelComboModal({ clients, onClose, onQueued }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [target, setTarget] = useState("");
  const [pickerTargets, setPickerTargets] = useState([]);
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [lastJob, setLastJob] = useState(null); // job persistente para polling
  const [jobDetail, setJobDetail] = useState(null);

  const client = clients.find(c => c.id === clientId);
  const isAll = target === "__ALL__";
  const expanded = isAll ? pickerTargets.map(t => t.value)
                          : (target ? [target] : []);

  useEffect(() => { setTarget(""); }, [clientId]);

  useEffect(() => {
    api.get("/scans/preset/threat-intel-combo").then(r => setInfo(r.data))
       .catch(() => setInfo(null));
  }, []);

  // Poll the running job until done/error (only for single-target mode)
  useEffect(() => {
    if (!lastJob) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/scans/jobs/${lastJob.id}`);
        if (cancelled) return;
        setJobDetail(r.data);
        if (r.data.status !== "done" && r.data.status !== "error") {
          setTimeout(tick, 2000);
        }
      } catch (_) { /* silencioso */ }
    };
    tick();
    return () => { cancelled = true; };
  }, [lastJob]);

  const submit = async (e) => {
    e.preventDefault();
    if (!authorized || expanded.length === 0) return;
    setBusy(true);
    setJobDetail(null);
    let firstJob = null, ok = 0, fail = 0;
    try {
      for (const tgt of expanded) {
        try {
          const r = await api.post("/scans/preset/threat-intel-combo", {
            client_id: clientId, target: tgt, authorized: true,
          });
          ok++;
          if (!firstJob) firstJob = r.data;
        } catch { fail++; }
      }
      if (ok === 0) {
        toast.error("No se pudo lanzar ninguna batería");
      } else if (expanded.length === 1) {
        setLastJob(firstJob);
        toast.success("Batería Threat Intel encolada");
        onQueued && onQueued();
      } else {
        toast.success(`${ok} baterías encoladas${fail ? ` · ${fail} fallaron` : ""} · revisa la Cronología`);
        onQueued && onQueued();
      }
    } finally { setBusy(false); }
  };

  const verdictColor = (v) => ({
    malicious:  "text-sev-critical border-sev-critical/50 bg-sev-critical/10",
    suspicious: "text-sev-medium   border-sev-medium/50   bg-sev-medium/10",
    clean:      "text-sev-low      border-sev-low/40      bg-sev-low/10",
    skipped:    "text-muted-foreground border-border      bg-background/40",
    error:      "text-sev-critical border-sev-critical/40 bg-sev-critical/5",
    unknown:    "text-muted-foreground border-border      bg-background/40",
  }[v] || "text-muted-foreground border-border bg-background/40");

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl bg-card border border-border max-h-[92vh] flex flex-col"
           data-testid="ti-combo-preset-modal">
        <div className="p-5 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="overline mb-1 flex items-center gap-2 text-sev-medium">
              <Radar className="w-3 h-3" /> Preset · Batería Threat Intel
            </div>
            <h2 className="font-display text-2xl font-medium">4 fuentes en paralelo · 1 hallazgo consolidado</h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
              Lanza VirusTotal + AbuseIPDB + GreyNoise + HackerTarget al mismo tiempo sobre un target,
              correlaciona los veredictos y persiste UN solo hallazgo con la severidad ajustada automáticamente.
              Las fuentes sin API key se saltan silenciosamente.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted" data-testid="ti-combo-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {info && (
            <div className="p-5 border-b border-border">
              <div className="overline mb-2">Fuentes configuradas · {info.available_count}/4 disponibles</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {info.sources.map(s => (
                  <div key={s.id}
                       className={cn("border p-2 font-mono text-[10px] tracking-widest uppercase",
                                     s.available ? "border-sev-low/40 text-sev-low bg-sev-low/5"
                                                 : "border-border text-muted-foreground bg-background/40")}
                       data-testid={`ti-source-${s.id}`}>
                    {s.available ? "· " : "○ "}{s.label}
                    <div className="text-[9px] normal-case tracking-normal mt-0.5">
                      {s.available ? "API key configurada" : "sin API key — se salta"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="overline mb-1">Cliente</div>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                        className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                        data-testid="ti-combo-client-select">
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="overline mb-1">Objetivo (dominio o IP)</div>
                <AssetTargetPicker clientId={clientId} value={target}
                                   onChange={(v, list) => { setTarget(v); setPickerTargets(list); }}
                                   targetType="any"
                                   allowAll={true}
                                   testId="ti-combo-target-picker" />
              </label>
            </div>

            {client && (
              <div className="text-[10px] font-mono text-muted-foreground">
                Alcance autorizado: {(client.root_domains || []).join(", ") || "sin dominios"}
                {(client.ip_ranges || []).length > 0 && ` · IPs: ${client.ip_ranges.join(", ")}`}
              </div>
            )}

            <label className="flex items-start gap-3 text-xs cursor-pointer">
              <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)}
                     data-testid="ti-combo-auth-checkbox"
                     className="mt-0.5 accent-primary" />
              <span>
                Declaro que dispongo de <b>autorización escrita</b> del cliente para consultar
                fuentes públicas de inteligencia de amenazas sobre <code className="font-mono text-primary">{isAll ? `${expanded.length} activos` : (target || "el objetivo")}</code>.
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose}
                      className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                Cancelar
              </button>
              <button type="submit" disabled={busy || !authorized || expanded.length === 0 || !clientId}
                      className="bg-sev-medium text-primary-foreground px-4 py-2 text-sm hover:bg-sev-medium/90 disabled:opacity-50 inline-flex items-center gap-2"
                      data-testid="ti-combo-submit-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
                {isAll ? `Lanzar ${expanded.length} baterías` : "Lanzar batería"}
              </button>
            </div>
          </form>

          {jobDetail && (
            <div className="p-5 border-t border-border space-y-3" data-testid="ti-combo-result">
              <div className="flex items-center justify-between">
                <div className="overline">Resultado de la correlación</div>
                <span className={cn("font-mono text-[10px] tracking-widest uppercase border px-2 py-0.5",
                                    jobDetail.status === "done" ? "text-sev-low border-sev-low/40 bg-sev-low/10"
                                    : jobDetail.status === "error" ? "text-sev-critical border-sev-critical/40 bg-sev-critical/10"
                                    : "text-primary border-primary/40 bg-primary/10")}
                      data-testid="ti-combo-status">
                  {jobDetail.status}
                </span>
              </div>

              {jobDetail.status !== "done" && jobDetail.status !== "error" && (
                <div className="flex items-center gap-2 text-muted-foreground text-xs">
                  <Loader2 className="w-3 h-3 animate-spin" /> Consultando las 4 fuentes…
                </div>
              )}

              {jobDetail.status === "done" && jobDetail.meta && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                    <StatBadge label="Maliciosas" value={jobDetail.meta.malicious_count || 0} tone="sev-critical" />
                    <StatBadge label="Sospechosas" value={jobDetail.meta.suspicious_count || 0} tone="sev-medium" />
                    <StatBadge label="Limpias" value={jobDetail.meta.clean_count || 0} tone="sev-low" />
                    <StatBadge label="Sin datos" value={jobDetail.meta.skipped_count || 0} tone="muted" />
                  </div>
                  <div className="space-y-1">
                    {(jobDetail.meta.sources || []).map(s => (
                      <div key={s.id} className={cn("border px-3 py-2 flex items-center justify-between gap-3",
                                                    verdictColor(s.verdict))}
                           data-testid={`ti-verdict-${s.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] tracking-widest uppercase w-24">{s.label}</span>
                          <span className="font-mono text-[10px] tracking-widest uppercase border px-1.5 py-0.5">{s.verdict}</span>
                        </div>
                        <span className="text-[11px] font-mono opacity-80 truncate">
                          {s.signal || s.reason || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    Hallazgo consolidado guardado en el job <code className="text-foreground">{jobDetail.id.slice(0, 8)}</code> ·
                    duración {jobDetail.duration_ms}ms · {jobDetail.findings_count} finding
                    {jobDetail.findings_count === 1 ? "" : "s"}.
                  </div>
                </>
              )}

              {jobDetail.status === "error" && (
                <div className="border border-sev-critical/40 bg-sev-critical/10 text-sev-critical p-3 text-xs font-mono">
                  {jobDetail.error || "Error desconocido"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBadge({ label, value, tone }) {
  const toneCls = {
    "sev-critical": "text-sev-critical border-sev-critical/40 bg-sev-critical/10",
    "sev-medium":   "text-sev-medium border-sev-medium/40 bg-sev-medium/10",
    "sev-low":      "text-sev-low border-sev-low/40 bg-sev-low/10",
    "muted":        "text-muted-foreground border-border bg-background/40",
  }[tone] || "text-muted-foreground border-border bg-background/40";
  return (
    <div className={cn("border p-2", toneCls)}>
      <div className="font-display text-2xl font-medium">{value}</div>
      <div className="font-mono text-[9px] tracking-widest uppercase">{label}</div>
    </div>
  );
}


